import { useState } from 'react';
import { useMessageService } from 'framework/ui/Message';
import useOutcomesService from 'domain/projects/newProject/businessOutcomes/useOutcomesService';
import useRiskService from 'domain/projects/newProject/risks/useRiskService';
import useMilestonesService from 'domain/projects/newProject/milestones/useMilestonesService';
import useStakeholdersService from 'domain/projects/newProject/stakeholders/useStakeholdersService';
import useAssumptionsService from 'domain/projects/newProject/assumptions/assumptionsService';
import { BusinessOutcomesType } from 'domain/projects/newProject/businessOutcomes/types';
import { MilestonesType } from 'domain/projects/newProject/milestones/types';
import { RisksType } from 'domain/shared/newProject';
import { StakeholderSearchResultItem } from 'domain/projects/newProject/stakeholders/types';
import { AssumptionType } from 'domain/projects/newProject/assumptions/types';
import { ReuseGroup } from './types';
import {
  reuseGroupConfig,
  runTransferTasks,
  buildTransferSuccessMessage,
  buildTransferErrorMessage,
  TRANSFER_SUCCESS_CONTEXT,
  TRANSFER_ERROR_CONTEXT,
} from './utils';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';

export interface TransferFormValues {
  businessOutcomes: BusinessOutcomesType[];
  milestones: MilestonesType[];
  risks: RisksType[];
  stakeholders: StakeholderSearchResultItem[];
  assumptions: AssumptionType[];
}

const useReuseDataService = (targetProjectId: string, projectManager?: string) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { businessOutcomesBatch } = useOutcomesService();
  const { milestonesBatch } = useMilestonesService();
  const { risksBatch } = useRiskService();
  const { stakeholdersBatch } = useStakeholdersService();
  const { createAssumptionsBatch } = useAssumptionsService();
  const { addMessage: addSuccessMessage, cleanMessage: cleanSuccessMessage } =
    useMessageService(TRANSFER_SUCCESS_CONTEXT);
  const { addMessage: addErrorMessage, cleanMessage: cleanErrorMessage } = useMessageService(TRANSFER_ERROR_CONTEXT);
  const { getUserAlias } = useAuthenticatedUserService();

  type BatchFn = (projectId: string, items: unknown[]) => Promise<unknown>;
  const batchByGroup: Record<ReuseGroup, BatchFn> = {
    businessOutcomes: businessOutcomesBatch as BatchFn,
    milestones: milestonesBatch as BatchFn,
    risks: risksBatch as BatchFn,
    stakeholders: stakeholdersBatch as BatchFn,
    assumptions: createAssumptionsBatch as BatchFn,
  };

  const transferData = async (formValues: TransferFormValues, groups: ReuseGroup[]): Promise<ReuseGroup[]> => {
    const userAlias = (await getUserAlias()) ?? undefined;

    const tasks = groups.reduce<Record<string, Promise<unknown>>>((acc, group) => {
      const payload = reuseGroupConfig[group].mapForCreate(formValues[group], { projectManager, userAlias });
      if (payload.length) acc[group] = batchByGroup[group](targetProjectId, payload);
      return acc;
    }, {});

    setIsSubmitting(true);
    cleanSuccessMessage();
    cleanErrorMessage();
    try {
      const { succeeded, failed } = await runTransferTasks(tasks);
      const toLabels = (result: string[]) => result.map(group => reuseGroupConfig[group as ReuseGroup].label);
      const successMessage = buildTransferSuccessMessage(toLabels(succeeded));
      const errorMessage = buildTransferErrorMessage(toLabels(failed));
      if (successMessage) addSuccessMessage(successMessage);
      if (errorMessage) addErrorMessage(errorMessage);
      return succeeded as ReuseGroup[];
    } finally {
      setIsSubmitting(false);
    }
  };

  return { transferData, isSubmitting };
};

export default useReuseDataService;
